import { ReactNode } from 'react';

import { fontStyles } from '@/styles/typography';

import { VerificationIcon } from '@/ui/Icons/VerificationIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

type ChildRowIssuerBadgeProps = {
  issuerName: string;
  tooltipContent?: ReactNode;
};

function ChildRowIssuerBadgeBody({
  issuerName,
  describedBy,
}: {
  issuerName: string;
  describedBy?: string;
}) {
  return (
    <div
      aria-label={`Issued by ${issuerName}`}
      aria-describedby={describedBy}
      className="bg-token-gray-200 border-token-gray-500 text-token-gray-800 flex h-[18px] min-w-[60px] cursor-default items-center justify-center gap-0.5 rounded-[26px] border border-solid pl-1 pr-1.5 mobile:min-w-[100px] hover:bg-token-gray-300 hover:border-token-gray-600"
    >
      <VerificationIcon className="size-2.5 text-token-gray-700 shrink-0" />
      <span
        className={fontStyles({
          variant: 'LABEL_XS',
          class: 'text-token-gray-800 before:content-none after:content-none',
        })}
      >
        <span className="hidden mobile:inline">{`Issued by ${issuerName}`}</span>
        <span className="inline mobile:hidden">{`by ${issuerName}`}</span>
      </span>
    </div>
  );
}

export function ChildRowIssuerBadge({ issuerName, tooltipContent }: ChildRowIssuerBadgeProps) {
  if (!tooltipContent) {
    return <ChildRowIssuerBadgeBody issuerName={issuerName} />;
  }

  const tooltipId = `${issuerName.toLowerCase().replace(/\s+/g, '-')}-help-text`;

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <span className="inline-flex shrink-0">
          <ChildRowIssuerBadgeBody issuerName={issuerName} describedBy={tooltipId} />
        </span>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>
        <span id={tooltipId}>{tooltipContent}</span>
      </MatchaTooltipContent>
      <MatchaTooltipPopover
        sideOffset={8}
        className="max-w-[246px] inline-block tablet:text-center"
      />
      <MatchaTooltipBottomSheet header={`Issued by ${issuerName}`} />
    </MatchaTooltip>
  );
}
